import { ReactComponentElement, useMemo } from "react";
import { useSelector } from "react-redux";

import { ThemeProvider } from "@seneca/senekit";

import { hasLiveSubscription } from "seneca-common/features/subscriptions/state";

import useDarkMode from "features/dark-mode/hooks/useDarkMode";
import { linkToParentDashboard } from "features/parents/links";
import { useIsPlurallApp } from "features/plurall";
import { getPreferredThemeSet } from "features/user/selectors";

import { getTheme } from "./selectors/getTheme";

type Props = {
  children: ReactComponentElement<any, any> | ReactComponentElement<any, any>[];
};

export default function AppThemeProvider({ children }: Props) {
  const isPlurallApp = useIsPlurallApp();

  const { pathname } = window.location;
  const isOnParentPlatform = pathname.includes(linkToParentDashboard());

  const userHasSubscription = useSelector(hasLiveSubscription);
  const preferredThemeSet = useSelector(getPreferredThemeSet);

  const { darkModeEnabled } = useDarkMode();

  const theme = useMemo(
    () =>
      getTheme({
        isPlurallApp,
        isOnParentPlatform,
        userHasSubscription,
        isDarkMode: darkModeEnabled,
        preferredThemeSet
      }),
    [
      isPlurallApp,
      isOnParentPlatform,
      userHasSubscription,
      darkModeEnabled,
      preferredThemeSet
    ]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
